import { useEffect, useRef } from "react";
import { $getNodeByKey } from "lexical";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

/**
 * Reusable hook to handle MathNode selection and Lexical editor onChange updates
 */
export function useLexicalMathNodeHandler({
  activeWorkboard,
  editorActiveWorkboardNameRef,
  globleOnChangeDataSave,
  isNotSavePopUp,
  setGlobleOnChangeDataSave,
  updateGlobleOnChangeData,
  updateWorkboardDataByName,
  setShowInputTooltip,
  setLatextextvalue,
  setOldMathNode,
  location,
  hasSeededLessonRef
}) {
  const [editor] = useLexicalComposerContext();

  // Persists for the life of this component instance. Doesn't reset when the
  // effect below tears down/re-registers due to unstable updateWorkboardDataByName
  // / updateGlobleOnChangeData references — only a real unmount clears it.

  useEffect(() => {
    // Handles click event for selecting MathNode
    const handleClick = (event) => {
      event.stopPropagation();

      const parentSpan = event.target.closest(".math-node");

      if (parentSpan) {
        const nodeKey = parentSpan.getAttribute("data-lexical-key");
        setOldMathNode(nodeKey);

        if (nodeKey) {
          editor.update(() => {
            const nodeToDelete = $getNodeByKey(nodeKey);

            if (nodeToDelete) {
              const latexValue = nodeToDelete.getTextContent();
              if (latexValue) {
                setLatextextvalue(latexValue);
              }
              if (parentSpan.className.includes("selected-math-node")) {
                parentSpan.classList.remove("selected-math-node");
                setShowInputTooltip(true);
              } else {
                parentSpan.className += " selected-math-node";
                setShowInputTooltip(true);
              }
            }
          });
        }
      }
    };

    document.addEventListener("click", handleClick);

    // Register Lexical update listener
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        // Ref is updated synchronously in UpdateActiveWorkboard `update()` before editor.update;
        // `activeWorkboard` from React can still be the previous tab on this same tick.
        const workboardName =
          editorActiveWorkboardNameRef?.current ?? activeWorkboard?.name;

        if (workboardName === "Assignment") {
          const globleOnChange = JSON.parse(
            sessionStorage.getItem("globleOnChangeData")
          );

          if (!globleOnChangeDataSave) {
            if (!isNotSavePopUp) {

              updateGlobleOnChangeData && updateGlobleOnChangeData(workboardName, html);
            }
          } else {
            setGlobleOnChangeDataSave(false);
          }
        } else {
          updateGlobleOnChangeData && updateGlobleOnChangeData(workboardName, html);
        }

        if (!hasSeededLessonRef.current) {
          // First real update since mount — seed "Lesson" from router state
          // instead of whatever the editor produced on its first paint.
          hasSeededLessonRef.current = true;
          updateWorkboardDataByName &&
            updateWorkboardDataByName(
              "Lesson",
              location?.state?.lesson?.description
            );
        } else {
          updateWorkboardDataByName &&

            updateWorkboardDataByName(workboardName, html);
        }
      });
    });

    // Cleanup on unmount
    return () => {
      document.removeEventListener("click", handleClick);
      unregister();
    };
  }, [
    editor,
    activeWorkboard,
    editorActiveWorkboardNameRef,
    globleOnChangeDataSave,
    isNotSavePopUp,
    setGlobleOnChangeDataSave,
    updateGlobleOnChangeData,
    updateWorkboardDataByName,
    setShowInputTooltip,
    setLatextextvalue,
    setOldMathNode,
    location,
  ]);
}